import { useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import PageTitle from '../../components/shared/PageTitle';
import {
  useConnectGoogleEmailMutation,
  useConnectMicrosoftEmailMutation,
} from '../../store/vendorApi';

export default function EmailIntegrationCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { provider } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  const [connectGoogle, { isLoading: isGoogleLoading, isSuccess: isGoogleSuccess, isError: isGoogleError }] = useConnectGoogleEmailMutation();
  const [connectMicrosoft, { isLoading: isMsLoading, isSuccess: isMsSuccess, isError: isMsError }] = useConnectMicrosoftEmailMutation();

  const isLoading = isGoogleLoading || isMsLoading;
  const isSuccess = isGoogleSuccess || isMsSuccess;
  const isError = isGoogleError || isMsError || !!error;

  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    if (error) {
      toast.error(t('settings.emailCallback.authDeniedToast'));
      return;
    }
    if (!code) {
      toast.error(t('settings.emailCallback.noCode'));
      return;
    }

    exchangedRef.current = true;

    const exchange = async () => {
      try {
        if (provider === 'google') {
          await connectGoogle({ code }).unwrap();
          toast.success(t('settings.emailCallback.googleSuccess'));
        } else if (provider === 'microsoft') {
          await connectMicrosoft({ code }).unwrap();
          toast.success(t('settings.emailCallback.microsoftSuccess'));
        } else {
          toast.error(t('settings.emailCallback.unknownProvider'));
        }
      } catch {
        toast.error(t('settings.emailCallback.connectError'));
      }
    };

    exchange();
  }, [code, error, provider, connectGoogle, connectMicrosoft, t]);

  useEffect(() => {
    if (isSuccess || isError || error) {
      const timer = setTimeout(() => navigate('/settings/email-integration', { replace: true }), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, isError, error, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
      <PageTitle title={t('settings.emailCallback.pageTitle')} />
      <div className="bg-white rounded-xl shadow-md p-10 max-w-md w-full text-center">
        {isLoading && (
          <>
            <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('settings.emailCallback.connecting')}</h2>
            <p className="text-gray-500">{t('settings.emailCallback.connectingHelp')}</p>
          </>
        )}

        {isSuccess && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('settings.emailCallback.successHeading')}</h2>
            <p className="text-gray-500">{t('settings.emailCallback.successRedirect')}</p>
          </>
        )}

        {isError && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('settings.emailCallback.failedHeading')}</h2>
            <p className="text-gray-500 mb-4">
              {error ? t('settings.emailCallback.authDenied') : t('settings.emailCallback.genericError')}
            </p>
            <button
              onClick={() => navigate('/settings/email-integration', { replace: true })}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              {t('settings.emailCallback.backToSettings')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
